import { useRef, useState } from 'react'
import { Modal, Button, useToast } from '@datahub/design-system'
import { post, downloadCsv } from '../../lib/api'

interface ImportIssue {
  line: number
  column?: string
  message: string
}

interface ImportPreview {
  totalRows: number
  companies: number
  contacts: number
  errors: ImportIssue[]
  sample: { legalCompanyName: string; displayName: string; contacts: string[] }[]
  created?: { id: string; displayName: string }[]
}

/** A column note that does not stop the import, unlike everything else in `errors`. */
function isIgnorable(issue: ImportIssue): boolean {
  return issue.message.endsWith('will be ignored')
}

interface CompanyImportModalProps {
  open: boolean
  onClose: () => void
  onImported: () => void
}

/**
 * Bulk onboarding (CMP-01). The file is checked before anything is written and the
 * counts shown, so an admin commits a file they have already seen the result of. One
 * company per row, and a company listed twice is one company with two contacts.
 */
export function CompanyImportModal({ open, onClose, onImported }: CompanyImportModalProps) {
  const toast = useToast()
  const fileRef = useRef<HTMLInputElement>(null)
  const [csv, setCsv] = useState('')
  const [fileName, setFileName] = useState('')
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [busy, setBusy] = useState(false)

  const blocking = (preview?.errors ?? []).filter((e) => !isIgnorable(e))
  const warnings = (preview?.errors ?? []).filter(isIgnorable)
  const ready = Boolean(preview) && blocking.length === 0 && (preview?.companies ?? 0) > 0

  function reset() {
    setCsv('')
    setFileName('')
    setPreview(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  async function check(text: string) {
    setBusy(true)
    try {
      setPreview(await post<ImportPreview>('/admin/companies/import?dryRun=true', { csv: text }))
    } catch (err) {
      setPreview(null)
      toast.error(err instanceof Error ? err.message : 'That file could not be read')
    } finally {
      setBusy(false)
    }
  }

  async function onFile(file: File) {
    const text = await file.text()
    setCsv(text)
    setFileName(file.name)
    await check(text)
  }

  async function commit() {
    setBusy(true)
    try {
      const result = await post<ImportPreview>('/admin/companies/import?dryRun=false', { csv })
      const created = result.created?.length ?? 0
      toast.success(`${created} ${created === 1 ? 'company' : 'companies'} created as drafts`)
      reset()
      onImported()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The import did not complete')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose() }}
      title="Import companies"
      primaryLabel={ready ? `Create ${preview!.companies} ${preview!.companies === 1 ? 'company' : 'companies'}` : 'Create'}
      primaryDisabled={!ready || busy}
      primaryLoading={busy}
      onPrimary={() => void commit()}
    >
      <div className="flex flex-col" style={{ gap: 20 }}>
        <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '18px' }}>
          Four columns: display name, legal company name, contact name, contact email. One company per row. To give a
          company more than one contact, repeat it on another row with the same legal company name.
        </p>

        <div>
          <Button
            variant="outline"
            size="small"
            onClick={() => void downloadCsv('/admin/companies/import/template', 'datahub-companies-template.csv')}
          >
            Download the template
          </Button>
        </div>

        <div className="flex flex-col" style={{ gap: 8 }}>
          <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>CSV file</span>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            aria-label="CSV file of companies"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void onFile(file)
            }}
            className="font-mulish text-white"
            style={{ fontSize: 12, background: '#1F2937', border: '0.5px solid #4B5563', padding: 10 }}
          />
          {fileName && (
            <span className="font-mulish text-text-support" style={{ fontSize: 10 }}>
              {fileName}{busy ? ' · checking…' : ''}
            </span>
          )}
        </div>

        {preview && (
          <div style={{ border: '0.5px solid #4B5563', background: '#1A1B1E' }}>
            <div className="flex items-center" style={{ padding: '8px 12px', borderBottom: '0.5px solid #4B5563', gap: 12 }}>
              <span className="font-mulish text-white" style={{ fontSize: 12 }}>
                {preview.totalRows} {preview.totalRows === 1 ? 'row' : 'rows'} · {preview.companies}{' '}
                {preview.companies === 1 ? 'company' : 'companies'} · {preview.contacts}{' '}
                {preview.contacts === 1 ? 'contact' : 'contacts'}
              </span>
              <span
                className="font-mulish"
                style={{ fontSize: 12, marginLeft: 'auto', color: blocking.length > 0 ? '#F05042' : '#7FBF7F' }}
              >
                {blocking.length > 0
                  ? `${blocking.length} ${blocking.length === 1 ? 'problem' : 'problems'} to fix`
                  : 'Ready to import'}
              </span>
            </div>

            {blocking.length > 0 && (
              <ul style={{ margin: 0, padding: '8px 12px', listStyle: 'none', maxHeight: 200, overflowY: 'auto' }}>
                {blocking.map((issue, i) => (
                  <li key={`${issue.line}-${issue.column}-${i}`} className="font-mulish" style={{ fontSize: 10, lineHeight: '16px', color: '#F05042' }}>
                    Line {issue.line}
                    {issue.column ? ` · ${issue.column}` : ''}: {issue.message}
                  </li>
                ))}
              </ul>
            )}

            {warnings.length > 0 && (
              <ul style={{ margin: 0, padding: '8px 12px', listStyle: 'none', borderTop: blocking.length > 0 ? '0.5px solid #4B5563' : undefined }}>
                {warnings.map((issue, i) => (
                  <li key={`w-${i}`} className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '16px' }}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            )}

            {blocking.length === 0 && preview.sample.length > 0 && (
              <div className="flex flex-col" style={{ padding: '8px 12px', gap: 6 }}>
                {preview.sample.map((company) => (
                  <div key={company.legalCompanyName} className="flex" style={{ gap: 8 }}>
                    <span className="font-mulish text-white flex-shrink-0" style={{ fontSize: 10, width: '42%' }}>
                      {company.displayName}
                    </span>
                    <span className="font-mulish text-text-support" style={{ fontSize: 10, flex: 1 }}>
                      {company.contacts.join(', ')}
                    </span>
                  </div>
                ))}
                {preview.companies > preview.sample.length && (
                  <span className="font-mulish text-text-support" style={{ fontSize: 10 }}>
                    and {preview.companies - preview.sample.length} more
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <p className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '16px' }}>
          Every company is created as a draft. Nothing is written unless the whole file checks out, and no email is
          sent until you set a cadence and make a company active. Link each one to Stears afterwards to fill in the
          rest of its profile.
        </p>
      </div>
    </Modal>
  )
}
